import { useCallback, useMemo, useRef } from 'react';
import type { TOptions, StringMap, TFunction } from 'i18next';
import type { UseTranslationOptions } from 'react-i18next';
import { useTranslation as useTranslationPackage } from 'react-i18next';
import { get } from 'lodash-es';

import { getNodeEnv } from '@shared/utils/getEnvironmentVariables';
import type { TranslationNamespaces, TranslateFunction, TranslationsByNamespace } from '@locales/generated';
import { pseudoLocalizeString } from '@shared/core/i18n/pseudoLocalization/pseudoLocalizeString';
import { resolveSupportedLocale } from '@shared/core/i18n/utils';
import { I18N_FALLBACK_LOCALE } from '@shared/core/i18n/i18n.constants';
import { useFeatureValue } from '../featureFlags';
import { createI18nextTq } from '@withjoy/tq';
import type { QuickTaggedTemplateWithPlural } from '@withjoy/tq';

// prettier-ignore
export interface GetTranslation<T> {
  <P1 extends keyof T>(prop1: P1): T[P1];
  <P1 extends keyof T, P2 extends keyof T[P1]>(prop1: P1, prop2: P2): T[P1][P2];
  <P1 extends keyof T, P2 extends keyof T[P1], P3 extends keyof T[P1][P2]>(prop1: P1, prop2: P2, prop3: P3): T[P1][P2][P3];
  <P1 extends keyof T, P2 extends keyof T[P1], P3 extends keyof T[P1][P2], P4 extends keyof T[P1][P2][P3]>(prop1: P1, prop2: P2, prop3: P3, prop4: P4):T[P1][P2][P3][P4];
  <P1 extends keyof T, P2 extends keyof T[P1], P3 extends keyof T[P1][P2], P4 extends keyof T[P1][P2][P3], P5 extends keyof T[P1][P2][P3][P4]>( prop1: P1, prop2: P2, prop3: P3, prop4: P4, prop5: P5 ): T[P1][P2][P3][P4][P5];
  <P1 extends keyof T, P2 extends keyof T[P1], P3 extends keyof T[P1][P2], P4 extends keyof T[P1][P2][P3], P5 extends keyof T[P1][P2][P3][P4], P6 extends keyof T[P1][P2][P3][P4][P5]>( prop1: P1, prop2: P2, prop3: P3, prop4: P4, prop5: P5, prop6: P6 ): T[P1][P2][P3][P4][P5][P6];
  <P1 extends keyof T, P2 extends keyof T[P1], P3 extends keyof T[P1][P2], P4 extends keyof T[P1][P2][P3], P5 extends keyof T[P1][P2][P3][P4], P6 extends keyof T[P1][P2][P3][P4][P5], P7 extends keyof T[P1][P2][P3][P4][P5][P6]>(prop1: P1, prop2: P2, prop3: P3, prop4: P4, prop5: P5, prop6: P6, prop7: P7): T[P1][P2][P3][P4][P5][P6][P7];
  <P1 extends keyof T, P2 extends keyof T[P1], P3 extends keyof T[P1][P2], P4 extends keyof T[P1][P2][P3], P5 extends keyof T[P1][P2][P3][P4], P6 extends keyof T[P1][P2][P3][P4][P5], P7 extends keyof T[P1][P2][P3][P4][P5][P6], P8 extends keyof T[P1][P2][P3][P4][P5][P6][P7]>(prop1: P1, prop2: P2, prop3: P3, prop4: P4, prop5: P5, prop6: P6, prop7: P7, prop8: P8): T[P1][P2][P3][P4][P5][P6][P7][P8];
}

type LeafNodeValue = string | TranslateFunction;

type TransformedValue<T extends LeafNodeValue> = T | MappedTranslationObject<T> | (T | MappedTranslationObject<T>)[];
type MappedTranslationObject<T extends LeafNodeValue> = {
  [key: string]: TransformedValue<T>;
};
type PathComponent = string | number;

// What `t2` hands out: a whole branch, or the resolved string when the keys name a leaf.
type TranslatedBranch = string | MappedTranslationObject<string>;

type LeafNodeTransformer<T> = (fullPath: PathComponent[]) => T;

type Handlers<T> = {
  stringTransformer: LeafNodeTransformer<T>;
  functionTransformer: LeafNodeTransformer<T>;
  unsupportedTransformer: LeafNodeTransformer<T>;
};

export type QuickTranslate = QuickTaggedTemplateWithPlural<string>;

type UseTranslationValue<N extends TranslationNamespaces> = {
  t: GetTranslation<TranslationsByNamespace[N]>;

  /**
   * @deprecated Use `t` instead.
   */
  t2: GetTranslation<TranslationsByNamespace<string>[N]>;

  tq: QuickTranslate;
};

const nodeEnv = getNodeEnv();

const defaultStringResolve = (context: 'path' | 'branch' | 'transform', path: PathComponent[], options?: TOptions, defaultValue: string = '') => {
  if (nodeEnv.isDevelopment || nodeEnv.isTest) {
    throw new Error(`NOOP leaf transformer. Context: ${context}, full path: ${stringifyInput(path)}, options: ${stringifyInput(options)}`);
  }

  return defaultValue;
};

const prefixKeyWithNamespace = (namespace: string, keys: PathComponent[]) => `${namespace}:${keys.join('.')}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stringifyInput = (input: any) => JSON.stringify(input || {}, null, 2);

const createNoOpTransformer =
  (context: 'path' | 'branch' | 'transform'): LeafNodeTransformer<TranslateFunction> =>
  fullPath =>
  options => {
    if (nodeEnv.isDevelopment || nodeEnv.isTest) {
      throw new Error(`NOOP leaf transformer. Context: ${context}, full path: ${stringifyInput(fullPath)}, options: ${stringifyInput(options)}`);
    }
    return defaultStringResolve(context, fullPath, options);
  };

const push = (paths: PathComponent[], ...pathComponents: PathComponent[]) => {
  const clone = paths.slice();
  clone.push(...pathComponents);
  return clone;
};

const resolvePathComponents = (pathComponents: PathComponent[]) => {
  return pathComponents[pathComponents.length - 1];
};

const shouldImmediatelyTranslate = (fullPath: PathComponent[]) => {
  const key = resolvePathComponents(fullPath);
  return typeof key === 'string' && key.startsWith('_');
};

/**
 * EXPERIMENT: memoize resolved translations.
 *
 * Every `t()` call walks i18next's key resolution (cleanKey -> getPath -> getResource). Components
 * re-render far more often than translations change, so the same (language, namespace, key) triple
 * is resolved over and over. Only calls WITHOUT options are cached: options carry interpolation
 * values, counts and context, so their results are not a pure function of the key.
 *
 * Browser only. The i18n instance is a module singleton, so on the SSR server one cache would be
 * shared by every concurrent request; SSR also renders each component once, so there is nothing to
 * reuse there.
 */
const RESOLVED_TRANSLATION_CACHE_LIMIT = 8192;
const resolvedTranslations = new Map<string, string>();
let resolvedTranslationsWired = false;

const clearResolvedTranslations = () => resolvedTranslations.clear();

// Bumped alongside the string cache. The branch objects `t2` hands out hold eagerly resolved
// strings, so a language switch or a late-loading namespace invalidates them for the same reason.
let translationGeneration = 0;

const invalidateTranslationCaches = () => {
  clearResolvedTranslations();
  translationGeneration++;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wireResolvedTranslationInvalidation = (i18n: any) => {
  if (!__BROWSER__ || resolvedTranslationsWired || !i18n) return;
  resolvedTranslationsWired = true;
  // A language switch or a late-loading namespace both change what a key resolves to.
  i18n.on?.('languageChanged', invalidateTranslationCaches);
  i18n.store?.on?.('added', invalidateTranslationCaches);
  i18n.store?.on?.('removed', invalidateTranslationCaches);
};

// No options, or an options object carrying no interpolation values: the result is then a pure
// function of (language, key). `@withjoy/tq` always passes an object, empty when the template has
// no interpolations, so `undefined` alone would never match.
const hasNoTranslationOptions = (options: unknown): boolean => options === undefined || (typeof options === 'object' && options !== null && Object.keys(options).length === 0);

const readResolvedTranslation = (cacheKey: string) => (__BROWSER__ ? resolvedTranslations.get(cacheKey) : undefined);

const writeResolvedTranslation = (cacheKey: string, value: string) => {
  if (!__BROWSER__) return;
  if (resolvedTranslations.size >= RESOLVED_TRANSLATION_CACHE_LIMIT) {
    resolvedTranslations.delete(resolvedTranslations.keys().next().value as string);
  }
  resolvedTranslations.set(cacheKey, value);
};

const transformValue = <T extends LeafNodeValue>(input: StringMap, key: string, fullPath: PathComponent[], handlers: Handlers<T>): TransformedValue<T> => {
  const value = get(input, key);

  if (typeof value === 'string') {
    // A string value is one base case and should be relegated to the handler
    return handlers.stringTransformer(fullPath);
  } else if (typeof value === 'function') {
    // A function value is another base case and should be relegated to the handler
    return handlers.functionTransformer(fullPath);
  } else if (Array.isArray(value)) {
    // Iterate over each item in the array
    return value.map((item: StringMap, index) => transformValue({ dummy: item }, 'dummy', push(fullPath, index), handlers)) as TransformedValue<T>;
  } else if (!value || typeof value !== 'object') {
    return handlers.unsupportedTransformer(fullPath);
  } else {
    // Iterate over the object keys
    const nestedObject = Object.keys(value).reduce((nestedAcc, nestedKey) => {
      nestedAcc[nestedKey] = transformValue<T>(value, nestedKey, push(fullPath, nestedKey), handlers);
      return nestedAcc;
    }, {} as MappedTranslationObject<T>);
    return nestedObject;
  }
};

// A bundle is usable as a schema only if it is a real object we can walk for its key structure.
const isWalkableBundle = (bundle: unknown): bundle is StringMap => !!bundle && typeof bundle === 'object';

const createTranslationObject = <T extends LeafNodeValue>(input: StringMap, handlers: Handlers<T>) => {
  return Object.keys(input).reduce((acc, key) => {
    acc[key] = transformValue(input, key, [key], handlers);
    return acc;
  }, {} as MappedTranslationObject<T>);
};

export function useTranslation<N extends TranslationNamespaces>(namespace: N, options?: UseTranslationOptions): UseTranslationValue<N> {
  // Legit dynamic forwarding: `namespace` is the literal a caller already passed to this hook (enforced
  // by joy's i18n literal-namespace lint rule). The internal pass-through can't itself be a literal.
  // eslint-disable-next-line no-restricted-syntax
  const [origTFunction, i18n] = useTranslationPackage(namespace, options);
  const currentLanguage = i18n.language;
  // Canonicalize before the walk: i18n.language is normally a canonical supported code, but
  // changeLanguage callers and detection edge cases can surface siblings (de-AT) — resolve to
  // the bundle key actually on disk (de-AT → de-DE). See PRNT-4468 / PR #15943.
  const walkLanguage = resolveSupportedLocale(currentLanguage).locale;

  // The typed `t`/`t2` accessor tree is built by walking a locale bundle whose key structure
  // matches the generated types. Historically this HAD to be the en-US bundle (the types'
  // source), which forced en-US into every client's store (see PR #16457 — the "omit en-US"
  // rounds). The prod verify-translations gate (#16531) now guarantees every supported locale
  // ⊇ en-US keys, so the CURRENT language's bundle is an equally valid schema — and it is
  // already loaded: react-i18next's useSuspense readiness gates on the current language's
  // namespace client-side, and the server holds all locales in memory.
  const currentLanguageBundle = i18n.getResourceBundle(walkLanguage, namespace);

  // A new namespace ships en-US-only until the translation bot backfills the rest, so the current
  // language's bundle can be permanently absent — walk en-US's equally valid schema, not suspend.
  const schemaBundle = isWalkableBundle(currentLanguageBundle) ? currentLanguageBundle : i18n.getResourceBundle(I18N_FALLBACK_LOCALE, namespace);

  // No walkable bundle at all: suspend on a reload (thrown Promise = React Suspense integration)
  // rather than crash the tree. Reload the FALLBACK too — in prod `fallbackLng` is false, so en-US
  // is never fetched for a non-English user, and reloading only their locale retries a file that
  // will never exist, suspending forever.
  if (!isWalkableBundle(schemaBundle)) {
    const languagesToReload = walkLanguage === I18N_FALLBACK_LOCALE ? [walkLanguage] : [walkLanguage, I18N_FALLBACK_LOCALE];
    throw i18n.reloadResources(languagesToReload, [namespace]);
  }

  const pseudoLocalization = useFeatureValue('pseudoLocalization');
  const shouldPseudoLocalize = walkLanguage === I18N_FALLBACK_LOCALE && pseudoLocalization;

  wireResolvedTranslationInvalidation(i18n);

  const t: TFunction = useCallback<TFunction>(
    (key: string | string[], options?: TOptions | string) => {
      const isCacheable = hasNoTranslationOptions(options) && typeof key === 'string';
      const cacheKey = isCacheable ? `${currentLanguage}\u0000${shouldPseudoLocalize ? 'p' : ''}\u0000${key}` : '';

      if (isCacheable) {
        const cached = readResolvedTranslation(cacheKey);
        if (cached !== undefined) return cached;
      }

      // Pseudo-localize English strings in dev
      const resolved = shouldPseudoLocalize ? pseudoLocalizeString(origTFunction(key, options)) : origTFunction(key, options);

      if (isCacheable && typeof resolved === 'string') writeResolvedTranslation(cacheKey, resolved);
      return resolved;
    },
    [origTFunction, shouldPseudoLocalize, currentLanguage],
  );

  const translationObject = useMemo<MappedTranslationObject<TranslateFunction | string>>(() => {
    return createTranslationObject(schemaBundle || {}, {
      stringTransformer: fullPath => {
        if (shouldImmediatelyTranslate(fullPath)) {
          return t(prefixKeyWithNamespace(namespace, fullPath));
        }

        return options => {
          return t(prefixKeyWithNamespace(namespace, fullPath), options);
        };
      },
      functionTransformer: createNoOpTransformer('path'),
      unsupportedTransformer: fullPath => {
        return options => defaultStringResolve('transform', fullPath, options);
      },
    });
  }, [schemaBundle, namespace, t]);

  const getTranslationByPath = useCallback<GetTranslation<TranslationsByNamespace[N]>>(
    (...keys: string[]) => {
      return get(translationObject, keys);
    },
    [translationObject],
  );

  // TODO: Accept nested options arg and apply to matching branch key
  const buildTranslatedBranch = useCallback<GetTranslation<TranslationsByNamespace<string>[N]>>(
    (...keys: string[]) => {
      const tFunctionOrBranch = get(translationObject, keys);
      // The value of a top level key is the translation function, not a branch.
      if (typeof tFunctionOrBranch === 'function') {
        return tFunctionOrBranch();
      }

      if (!tFunctionOrBranch || typeof tFunctionOrBranch !== 'object') {
        // If the branch is missing (e.g. key doesn't exist in bundle yet),
        // return a NOOP transformer to avoid crashing.
        return createTranslationObject<string>(
          {},
          {
            stringTransformer: fullPath => t(prefixKeyWithNamespace(namespace, push(keys, ...fullPath))),
            functionTransformer: fullPath => t(prefixKeyWithNamespace(namespace, push(keys, ...fullPath))),
            unsupportedTransformer: fullPath => defaultStringResolve('branch', fullPath),
          },
        );
      }

      return createTranslationObject<string>(tFunctionOrBranch, {
        stringTransformer: fullPath => {
          return t(prefixKeyWithNamespace(namespace, push(keys, ...fullPath)));
        },
        functionTransformer: fullPath => {
          return t(prefixKeyWithNamespace(namespace, push(keys, ...fullPath)));
        },
        unsupportedTransformer: fullPath => {
          return defaultStringResolve('branch', fullPath);
        },
      });
    },
    [translationObject, namespace, t],
  );

  // `t2('sections')` built a new branch object on every call. The contents do not change while the
  // translations behind them do not — they are derived from `translationObject` — but the identity
  // did, so every `useMemo` listing a branch in its dependencies recomputed on every render.
  //
  // The cache is dropped when the builder changes (a new bundle, namespace or language) and when the
  // translation store is updated in place: `addResourceBundle(..., deep, overwrite)` merges into the
  // existing namespace object, so nothing a hook depends on has to change identity and there may be
  // no re-render to notice it. That is why the generation is checked here, on every call, rather
  // than used as a hook dependency — the branches hold eagerly resolved strings, so a late-loading
  // namespace would otherwise keep serving the text from before it arrived.
  //
  // Scope: this cache belongs to one `useTranslation` call in one component instance, so two
  // components asking for the same branch still get different objects. Within a component, treat a
  // branch as read-only — mutating it is visible to that component's later renders.
  const branchCacheRef = useRef<{
    builder: typeof buildTranslatedBranch;
    generation: number;
    byKeyPath: Map<string, TranslatedBranch>;
  } | null>(null);

  const getTranslatedBranch = useCallback<GetTranslation<TranslationsByNamespace<string>[N]>>(
    (...keys: string[]) => {
      const current = branchCacheRef.current;
      const cache =
        current && current.builder === buildTranslatedBranch && current.generation === translationGeneration
          ? current
          : { builder: buildTranslatedBranch, generation: translationGeneration, byKeyPath: new Map<string, TranslatedBranch>() };
      branchCacheRef.current = cache;

      const cacheKey = keys.join('\u0000');
      const cached = cache.byKeyPath.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }
      // GetTranslation is overloaded per key depth; the builder takes the same keys this wrapper was
      // handed, so the spread is safe even though the overloads cannot express it.
      const branch = (buildTranslatedBranch as (...branchKeys: string[]) => TranslatedBranch)(...keys);
      cache.byKeyPath.set(cacheKey, branch);
      return branch;
    },
    [buildTranslatedBranch],
  );

  const tq = useMemo(() => {
    return createI18nextTq(
      (key, _default, opts) => {
        const isCacheable = hasNoTranslationOptions(opts) && typeof key === 'string';
        // The namespace belongs in the key: the cache is module-global while `origTFunction` is bound to one
        // namespace, and a quick key is derived from the English source — so two namespaces sharing a string
        // collide on both `key` and `_default` while translating differently.
        const cacheKey = isCacheable ? `${currentLanguage}\u0000q\u0000${namespace}\u0000${key}\u0000${_default ?? ''}` : '';

        if (isCacheable) {
          const cached = readResolvedTranslation(cacheKey);
          if (cached !== undefined) return cached;
        }

        const resolved = origTFunction(key, _default, opts);
        if (isCacheable && typeof resolved === 'string') writeResolvedTranslation(cacheKey, resolved);
        return resolved;
      },
      { i18n },
    );
  }, [origTFunction, i18n, currentLanguage, namespace]);

  return {
    t: getTranslationByPath,
    t2: getTranslatedBranch,
    tq,
  };
}
